/**
 * ScrollToTopButton — flecha flotante "volver al inicio".
 *
 * Aparece después de que el usuario hace scroll más allá de `SCROLL_THRESHOLD_PX`
 * (500px). Click → scroll suave al tope (o `auto` si el usuario tiene
 * `prefers-reduced-motion: reduce`).
 *
 * Estética Ceciluz Arte (coherente con FloatingWhatsApp):
 *   - Forma circular, gradiente violeta noche → vívido, borde dorado fino,
 *     ícono crema. Pequeño destello dorado en la esquina superior derecha
 *     como guiño místico.
 *   - Mobile: 42×42 px, `bottom: 88px` (queda holgado encima del WhatsApp
 *     de 52 px que vive en `bottom: 16px`).
 *   - Desktop: 46×46 px, `bottom: 104px` (encima del WhatsApp de 60 px en
 *     `bottom: 28px`, con margen visual claro).
 *
 * A11y:
 *   - `<button type="button">`, `aria-label` y `title` con "Volver al inicio".
 *   - Cuando está oculto: `tabIndex=-1` + `aria-hidden=true` + `pointer-events-none`
 *     para no aparecer en navegación por teclado ni ser leído por SR.
 *   - Ícono decorativo con `aria-hidden="true"`.
 *   - Foco visible vía anillo dorado (`focus-visible:ring-gold-500`).
 *
 * Sin dependencias nuevas — `ArrowUp` viene del lucide-react ya instalado.
 * Cero hex literals — sólo tokens del Design System.
 */
import { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useFloatingHidden } from './floating-visibility';

/** Umbral de scroll vertical (px) a partir del cual el botón se hace visible. */
const SCROLL_THRESHOLD_PX = 500;

export function ScrollToTopButton() {
  const [pastThreshold, setPastThreshold] = useState(false);
  // Una sección (p. ej. el reproductor de la bienal) puede pedir ocultarlo.
  const hidden = useFloatingHidden();
  const isVisible = pastThreshold && !hidden;

  useEffect(() => {
    // Sincroniza el estado con el scroll actual al montar (por si el usuario
    // entró a una ruta ya scrolleada hacia abajo — ej. atrás del navegador).
    const onScroll = () => {
      setPastThreshold(window.scrollY > SCROLL_THRESHOLD_PX);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  function handleClick() {
    // WCAG 2.3.3 — respetamos `prefers-reduced-motion`. Si el usuario lo
    // tiene activo, hacemos scroll instantáneo (sin animación smooth).
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Volver al inicio"
      title="Volver al inicio"
      aria-hidden={!isVisible}
      tabIndex={isVisible ? 0 : -1}
      className={cn(
        // Posición — esquina inferior derecha, encima del FloatingWhatsApp.
        // Mobile : right 16px / bottom 88px  (WhatsApp 52 px en bottom-4=16).
        // Desktop: right 28px / bottom 104px (WhatsApp 60 px en md:bottom-7=28).
        // `z-[960]` (per spec del lead) mantiene el botón sobre footer y
        // contenido, pero debajo de modales/toasts del DS.
        // IMPORTANTE: NO agregar `relative` ni `static` aquí — Tailwind emite
        // `relative` después de `fixed` en su CSS layer y, al ser ambos de la
        // misma propiedad `position`, el segundo gana y se anula el modo
        // flotante (el botón aterriza en el flujo del DOM, típicamente al pie
        // de la página por la izquierda). `fixed` ya es contexto de
        // posicionamiento válido para el `<span absolute>` decorativo de abajo.
        'fixed z-[960] right-4 bottom-[88px] md:right-7 md:bottom-[104px]',
        // Forma circular y tamaño responsive (un pelín más pequeño que el
        // WhatsApp para que se lea como acción secundaria).
        'inline-flex items-center justify-center rounded-pill',
        'h-[42px] w-[42px] md:h-[46px] md:w-[46px]',
        // Fondo: gradiente violeta noche → vívido (coherente con FloatingWhatsApp).
        'bg-gradient-to-br from-primary-900 via-primary-800 to-primary-vivid',
        // Borde dorado fino + ícono crema.
        'border border-gold-500/60 text-cream-50',
        // Sombra elev2 base.
        'shadow-elev2',
        // Microtransición suave (el sistema de tokens anula `--duration-*`
        // automáticamente bajo prefers-reduced-motion).
        'transition-all duration-2 ease-out',
        // Estado oculto vs visible.
        isVisible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-2 pointer-events-none',
        // Hover: leve elevación + glow dorado + borde más cálido.
        'hover:-translate-y-0.5 hover:shadow-elev3 hover:border-gold-soft',
        // Active: pequeño "press".
        'active:scale-[0.96]',
        // Focus visible accesible (anillo dorado, no el azul del browser).
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100',
      )}
    >
      <ArrowUp className="h-5 w-5" aria-hidden="true" strokeWidth={2} />

      {/* Destello dorado decorativo en la esquina superior derecha — guiño
          místico coherente con sparkles del Navbar/Terapias hero. */}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -top-0.5 -right-0.5',
          'h-1.5 w-1.5 rounded-pill bg-gold-soft',
          'opacity-80',
        )}
      />
    </button>
  );
}
